/**
 * Copyright 2016, Fullstack.io, LLC.
 *
 * This source code is licensed under the MIT-style license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { Action } from 'redux';
import { Customer } from './customer.model';
import * as CustomerActions from './customer.actions';
import { createSelector } from 'reselect';
import { User } from '../user/user.model';
import { Information } from '../information/information.model';
import { Address } from '../address/address.model';
import { Telephone } from '../telephone/telephone.model';
import { log } from 'util';
import { stat } from 'fs';

/**
 * This file describes the state concerning Customers, how to modify it through
 * the reducer, and the selectors.
 */
export interface CustomersEntities {
  [id: string]: Customer;
}

export interface CustomersState {
  ids: string[];
  entities: CustomersEntities;
  currentCustomerId?: string;
};

const initialState: CustomersState = {
  ids: [],
  currentCustomerId: null,
  entities: {}
};

/**
 * Action addCustomer
 */

const addCustomer =
 function(state: CustomersState, action: Action): CustomersState {
  const customer: Customer = (<CustomerActions.AddCustomerAction>action).customer;  

  if (state.ids.includes(customer.customerId)) {
    return state;
  }

  return {
    ids: [ ...state.ids, customer.customerId ],
    currentCustomerId: state.currentCustomerId,
    entities: Object.assign({}, state.entities, {
      [customer.customerId]: customer
    })
  }
}

const deleteCustomer =
 function(state: CustomersState, action: Action): CustomersState {
  const customer: Customer = (<CustomerActions.AddCustomerAction>action).customer;  

  if (state.ids.includes(customer.customerId)) {
    delete state.entities[customer.customerId]
  }

  return {
    ids: state.ids.filter(id => id !== customer.customerId),
    currentCustomerId: state.currentCustomerId,
    entities: state.entities
  }
}


/**
 * Action selectCustomer
 */

const selectCustomer =
 function(state: CustomersState, action: Action): CustomersState {
  const customer: Customer = (<CustomerActions.SelectCustomerAction>action).customer;
  const oldCustomer = state.entities[customer.customerId];
  const newCustomer = Object.assign({}, oldCustomer, {})
  
  return {
    ids: [ ...state.ids, customer.customerId ],
    currentCustomerId: customer.customerId,
    entities: Object.assign({}, state.entities, {
      [customer.customerId]: newCustomer
    })
  }                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                               
}

/**
 * Action addInformations to Customer
 */

const addInformations =
  function(state: CustomersState, action: Action): CustomersState {
    const customer: Customer = (<CustomerActions.AddInformationsAction>action).customer;
    const information: Information = (<CustomerActions.AddInformationsAction>action).information;

    // grab the old customer from entities
    const oldCustomer = new Customer(state.entities[customer.customerId]);

    // create a new customer which has informations
    const newCustomer = Object.assign(
      {},
      oldCustomer,
      information
    )

    return {
      ids: state.ids,
      currentCustomerId: state.currentCustomerId,
      entities: Object.assign({}, state.entities, {
        [customer.customerId]: newCustomer
      })
    };
  }

/**
 * Action addAddress to Customer
 */

const addAddress =
  function(state: CustomersState, action: Action): CustomersState {
    const customer: Customer = (<CustomerActions.AddAddressAction>action).customer;
    const address: Address = (<CustomerActions.AddAddressAction>action).address;

    // grab the old customer from entities
    const oldCustomer = state.entities[customer.customerId];

    // create a new customer which has addresss
    const newCustomer = Object.assign({}, oldCustomer, {
        listEndereco: [ ...oldCustomer.listEndereco, address ]
      }
    )

    return {
      ids: state.ids,
      currentCustomerId: state.currentCustomerId,
      entities: Object.assign({}, state.entities, {
        [customer.customerId]: newCustomer
      })
    };
  }

/**
 * Action addTelephone to Customer
 */

const addTelephone =
  function(state: CustomersState, action: Action): CustomersState {
    const customer: Customer = (<CustomerActions.AddTelephoneAction>action).customer;
    const telephone: Telephone = (<CustomerActions.AddTelephoneAction>action).telephone; 

    // grab the old customer from entities
    const oldCustomer = state.entities[customer.customerId];

    // create a new customer which has telephones
    const newCustomer = Object.assign({}, oldCustomer, {
      listTelefone: [telephone, ...oldCustomer.listTelefone]
    }
  )

    return {
      ids: state.ids,
      currentCustomerId: state.currentCustomerId,
      entities: Object.assign({}, state.entities, {
        [customer.customerId]: newCustomer
      })
    };
  }

const updateTelephone =
  function(state: CustomersState, action: Action): CustomersState {
    const customer: Customer = (<CustomerActions.AddTelephoneAction>action).customer;
    const telephone: Telephone = (<CustomerActions.AddTelephoneAction>action).telephone; 

    // grab the old customer from entities
    const oldCustomer = state.entities[customer.customerId];

    // create a new customer which has telephones
    const newCustomer = Object.assign({}, oldCustomer, {
        listTelefone: [telephone, ...oldCustomer.listTelefone]
      }
    )

    return {
      ids: state.ids,
      currentCustomerId: state.currentCustomerId,
      entities: Object.assign({}, state.entities, {
        [customer.customerId]: newCustomer
      })
    };
  }

export const CustomersReducer =
  function(state: CustomersState = initialState, action: Action): CustomersState {
    switch (action.type) {
      case CustomerActions.ADD_CUSTOMER:
        return addCustomer(state, action)
      case CustomerActions.SELECT_CUSTOMER:
        return selectCustomer(state, action)
      case CustomerActions.ADD_INFORMATIONS:
        return addInformations(state, action);
      case CustomerActions.ADD_ADDRESS:
        return addAddress(state, action);
      case CustomerActions.ADD_TELEPHONE:
        return addTelephone(state, action);
      case CustomerActions.UPDATE_TELEPHONE:
        return updateTelephone(state, action);
      case CustomerActions.DEL_CUSTOMER:
        return deleteCustomer(state, action);
      default:
        return state;
    }
};

export const getCustomersState = (state): CustomersState => state.customers;

export const getCustomersEntities = createSelector(
  getCustomersState,
  ( state: CustomersState ) => state.entities );

export const getAllCustomers = createSelector(
  getCustomersEntities,
  (entities: CustomersEntities) => Object.keys(entities)
    .map((customerId) => entities[customerId])
)

export const getCurrentCustomer = createSelector(
  getCustomersEntities,
  getCustomersState,
  ( entities: CustomersEntities, state: CustomersState ) => 
      entities[state.currentCustomerId]);